import React, { useState, useEffect } from "react";
import "./Register.css";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import NavUser from "../components/NavUser";
import { FaArrowLeft, FaIdCard, FaFileAlt, FaCheckCircle, FaCloudUploadAlt } from "react-icons/fa";

function TravelerToAgent() {
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_API_URL;

  // FORM STATES
  const [nicNumber, setNicNumber] = useState("");
  const [address, setAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [dob, setDob] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [step, setStep] = useState(1);
  const [gender, setGender] = useState("");
  const [nationality, setNationality] = useState("");
  const [country, setCountry] = useState("");
  const [isCorrection, setIsCorrection] = useState(false);

  const [genders, setGenders] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [countries, setCountries] = useState([]);

  const [isRestrictedNonSriLankan, setIsRestrictedNonSriLankan] = useState(false);

  useEffect(() => {
    async function loadUserDetails() {
      try {
        const storedUser = JSON.parse(localStorage.getItem("user"));
        if (!storedUser || !storedUser.id) return;

        const res = await fetch(`${BACKEND_URL}/users/${storedUser.id}`);
        const user = await res.json();
        setPhoneNumber(user.phone_number || "");
        setDob(user.dob || "");
        setGender(user.gender_id || "");
        setNationality(user.nationality_id || "");
        setCountry(user.country_id || "");
        setFullName(user.full_name || "");
        setEmail(user.email || "");

        const countryName = (user.Country?.name || "").toLowerCase();
        if (user.country_id && !countryName.includes("sri lanka") && countryName.length > 0) {
          setIsRestrictedNonSriLankan(true);
        }
      } catch (err) {
        console.error("Failed to load user details:", err);
      }
    }

    async function loadMeta() {
      try {
        const [gRes, nRes, cRes] = await Promise.all([
          fetch(`${BACKEND_URL}/genders`),
          fetch(`${BACKEND_URL}/nationalities`),
          fetch(`${BACKEND_URL}/countries`)
        ]);
        if (gRes.ok) setGenders(await gRes.json());
        if (nRes.ok) {
          const all = await nRes.json();
          setNationalities(all.filter(n => n.name.toLowerCase().includes("sri lanka")));
        }
        if (cRes.ok) {
          const all = await cRes.json();
          setCountries(all.filter(c => c.name.toLowerCase().includes("sri lanka")));
        }
      } catch (err) {
        console.error("Meta load error:", err);
      }
    }

    loadUserDetails();
    loadMeta();

    // Check for step in URL
    const params = new URLSearchParams(window.location.search);
    const s = params.get("step");
    if (s) setStep(parseInt(s));
    
    const corr = params.get("correction");
    if (corr === "true") setIsCorrection(true);
  }, [BACKEND_URL]);

  // FILE STATES
  const [nicFront, setNicFront] = useState(null);
  const [nicBack, setNicBack] = useState(null);
  const [doc, setDoc] = useState(null);
  const [waterFront, setWaterFront] = useState(null);
  const [waterBack, setWaterBack] = useState(null);

  const [loading, setLoading] = useState(false);

  const handleFileChange = (e, setter) => {
    setter(e.target.files[0]);
  };

  const handleStep1 = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("jwt_token");
      const res = await fetch(`${BACKEND_URL}/agents/save-identity`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          national_id_number: nicNumber,
          address,
          phone_number: phoneNumber,
          dob,
          agent_type: "agent",
          gender_id: gender,
          nationality_id: nationality,
          country_id: country,
          full_name: fullName,
          email: email,
          city: city
        }),
      });

      if (res.ok) {
        if (isCorrection) {
          const user = JSON.parse(localStorage.getItem("user"));
          Swal.fire({ 
            title: "Updated!", 
            text: "Your personal details have been updated.", 
            icon: "success" 
          }).then(() => {
            navigate(`/travelerhome/${user?.id || ""}`);
          });
        } else {
          setStep(2);
          window.scrollTo(0, 0);
        }
      } else {
        const data = await res.json();
        Swal.fire({ title: "Update Failed", text: data.message || "Failed to save details.", icon: "error" });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({ title: "Error", text: "Network error occurred.", icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async (e) => {
    e.preventDefault();

    if (!nicFront || !nicBack || !waterFront || !waterBack) {
      Swal.fire({
        title: "Missing Documents",
        text: "Please upload all required verification documents.",
        icon: "warning",
        confirmButtonColor: "#3b82f6"
      });
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("jwt_token");
      const formData = new FormData();
      formData.append("nic_front", nicFront);
      formData.append("nic_back", nicBack);
      formData.append("water_bill_front", waterFront);
      formData.append("water_bill_back", waterBack);
      if (doc) formData.append("doc", doc);

      const res = await fetch(`${BACKEND_URL}/agents/save-verification`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (res.ok) {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const updatedUserObj = { ...user, role_id: 6, status_id: 3 };
        localStorage.setItem("user", JSON.stringify(updatedUserObj));

        Swal.fire({
          title: "Application Submitted!",
          text: "Your agent application is now complete and pending admin approval.",
          icon: "success",
          confirmButtonText: "Return Home",
          confirmButtonColor: "#3b82f6"
        }).then(() => {
          navigate(`/travelerhome/${user?.id || ""}`);
        });
      } else {
        const data = await res.json();
        Swal.fire({ title: "Upload Failed", text: data.message || "Failed to save documents.", icon: "error" });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({ title: "Error", text: "Network error occurred.", icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="agent-page-premium">
      <NavUser />
      
      <div className="premium-hero-mini">
        <div className="hero-content">
          <h1>Partner with Soloway</h1>
          <p>Join our elite network of agents and earn as you travel.</p>
        </div>
      </div>

      <div className="premium-container-wide">
        {isRestrictedNonSriLankan ? (
          <div className="premium-glass-card restriction-card" style={{ padding: "40px", textAlign: "center", maxWidth: "680px", margin: "40px auto", borderRadius: "16px", background: "#ffffff", boxShadow: "0 10px 30px rgba(0,0,0,0.08)", border: "1px solid #e2e8f0" }}>
            <div style={{ width: "80px", height: "80px", background: "#fef2f2", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
              <FaIdCard size={36} color="#ef4444" />
            </div>
            <h2 style={{ fontSize: "24px", fontWeight: "800", color: "#1e293b", marginBottom: "12px" }}>
              Restricted to Sri Lankan Resident Agents
            </h2>
            <p style={{ fontSize: "15px", color: "#64748b", lineHeight: "1.6", marginBottom: "24px" }}>
              Soloway Travel Agent representation is reserved for Sri Lankan resident agencies and coordinators. As an international traveler, you can access and book all services directly through the platform.
            </p>
            <div style={{ background: "#f8fafc", borderRadius: "12px", padding: "16px 20px", marginBottom: "30px", textAlign: "left", border: "1px solid #e2e8f0" }}>
              <p style={{ fontSize: "14px", color: "#334155", margin: 0, fontWeight: "600" }}>
                🌍 <strong>As an International Traveler, you can:</strong>
              </p>
              <ul style={{ fontSize: "13.5px", color: "#64748b", marginTop: "8px", paddingLeft: "20px", lineHeight: "1.6" }}>
                <li>Book hotels, villas, and verified accommodations in Sri Lanka</li>
                <li>Hire certified guides and join experiential group tours</li>
                <li>Apply for your Sri Lanka Electronic Travel Authorization (ETA)</li>
              </ul>
            </div>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button className="btn-premium-action" onClick={() => navigate(-1)} style={{ padding: "12px 24px" }}>
                ← Return to Dashboard
              </button>
            </div>
          </div>
        ) : (
        <div className="premium-glass-card">
          <div className="card-header-premium">
            <button onClick={() => navigate(-1)} className="btn-back-minimal">
              <FaArrowLeft />
            </button>
            <div className="header-titles">
              <h2>Become an Agent</h2>
              <p>Step up your journey. Complete your profile to become a certified Soloway Agent.</p>
            </div>
          </div>

          <div className="wizard-progress" style={{display: 'flex', gap: '10px', marginBottom: '30px', padding: '0 20px'}}>
            <div className={`step-indicator ${step === 1 ? 'active' : 'completed'}`} style={{flex: 1, height: '4px', borderRadius: '2px', background: step === 1 ? '#3b82f6' : '#10b981'}}></div>
            <div className={`step-indicator ${step === 2 ? 'active' : ''}`} style={{flex: 1, height: '4px', borderRadius: '2px', background: step === 2 ? '#3b82f6' : '#e2e8f0'}}></div>
          </div>

          <form className="premium-form-layout" onSubmit={step === 1 ? handleStep1 : handleStep2}>
            {step === 1 ? (
              <div className="form-column">
                <div className="section-header-minimal">
                  <FaIdCard /> <span>Identity Details</span>
                </div>

                {isCorrection && (
                  <div className="form-grid-2" style={{ gap: '20px', marginBottom: '15px' }}>
                    <div className="premium-field">
                      <label>Full Name</label>
                      <input
                        type="text"
                        placeholder="Enter full name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="premium-field">
                      <label>Email Address</label>
                      <input
                        type="email"
                        placeholder="Enter email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled // Typically email shouldn't be easily changed, but we allow viewing
                      />
                    </div>
                  </div>
                )}
                
                <div className="premium-field">
                  <label>National ID / Passport Number</label>
                  <input
                    type="text"
                    placeholder="Enter your identification number"
                    value={nicNumber}
                    onChange={(e) => setNicNumber(e.target.value)}
                    required
                  />
                </div>

                <div className="form-grid-2" style={{ gap: '20px' }}>
                  <div className="premium-field">
                    <label>Phone Number</label>
                    <input
                      type="text"
                      placeholder="Enter phone number"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      required
                    />
                  </div>
                  <div className="premium-field">
                    <label>Birth Date</label>
                    <input
                      type="date"
                      value={dob}
                      onChange={(e) => setDob(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="premium-field">
                  <label>Gender</label>
                  <select value={gender} onChange={(e) => setGender(e.target.value)} required>
                    <option value="">Select Gender</option>
                    {genders.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>

                <div className="form-grid-2" style={{ gap: '20px' }}>
                  <div className="premium-field">
                    <label>Nationality</label>
                    <select value={nationality} onChange={(e) => setNationality(e.target.value)} required>
                      <option value="">Select Nationality</option>
                      {nationalities.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
                    </select>
                  </div>
                  <div className="premium-field">
                    <label>Country</label>
                    <select value={country} onChange={(e) => setCountry(e.target.value)} required>
                      <option value="">Select Country</option>
                      {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>

                <div className={isCorrection ? "form-grid-2" : "premium-field"} style={isCorrection ? { gap: '20px' } : {}}>
                  <div className="premium-field">
                    <label>Permanent Residential Address</label>
                    <textarea
                      placeholder="Provide your full legal address"
                      rows="4"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      required
                    />
                  </div>
                  {isCorrection && (
                    <div className="premium-field">
                      <label>City</label>
                      <input
                        type="text"
                        placeholder="Enter your city"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        required
                      />
                    </div>
                  )}
                </div>

                <div className="action-row-centered" style={{marginTop: '40px'}}>
                  <button type="submit" className="btn-premium-action" disabled={loading}>
                    {loading ? (isCorrection ? "Updating..." : "Saving...") : (isCorrection ? "Update Details" : "Next: Documents")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="form-column">
                <div className="section-header-minimal">
                  <FaFileAlt /> <span>Verification Documents</span>
                </div>

                <div className="upload-grid-premium">
                  <div className="premium-upload-item">
                    <label>ID Front</label>
                    <label className={`upload-zone ${nicFront ? 'active' : ''}`}>
                      <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, setNicFront)} required />
                      <FaCloudUploadAlt />
                      <span>{nicFront ? nicFront.name : "Choose File"}</span>
                    </label>
                  </div>

                  <div className="premium-upload-item">
                    <label>ID Back</label>
                    <label className={`upload-zone ${nicBack ? 'active' : ''}`}>
                      <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, setNicBack)} required />
                      <FaCloudUploadAlt />
                      <span>{nicBack ? nicBack.name : "Choose File"}</span>
                    </label>
                  </div>

                  <div className="premium-upload-item">
                    <label>Utility Bill (Front)</label>
                    <label className={`upload-zone ${waterFront ? 'active' : ''}`}>
                      <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, setWaterFront)} required />
                      <FaCloudUploadAlt />
                      <span>{waterFront ? waterFront.name : "Choose File"}</span>
                    </label>
                  </div>

                  <div className="premium-upload-item">
                    <label>Utility Bill (Back)</label>
                    <label className={`upload-zone ${waterBack ? 'active' : ''}`}>
                      <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, setWaterBack)} required />
                      <FaCloudUploadAlt />
                      <span>{waterBack ? waterBack.name : "Choose File"}</span>
                    </label>
                  </div>
                </div>

                <div className="premium-field full-width" style={{ marginTop: '20px' }}>
                  <label>Business Registration (Optional)</label>
                  <label className={`upload-zone-wide ${doc ? 'active' : ''}`}>
                    <input type="file" accept="image/*,.pdf" onChange={(e) => handleFileChange(e, setDoc)} />
                    <FaCloudUploadAlt />
                    <span>{doc ? doc.name : "Upload supporting business document (PDF/Image)"}</span>
                  </label>
                </div>

                <div className="action-row-centered" style={{marginTop: '40px', gap: '20px'}}>
                  {!isCorrection && (
                    <button type="button" onClick={() => setStep(1)} className="btn-back-minimal" style={{border: '1px solid #ddd', padding: '10px 20px', borderRadius: '8px'}}>
                      Back to Details
                    </button>
                  )}
                  <button type="submit" className="btn-premium-action" disabled={loading}>
                    {loading ? (
                      <span className="loader-dots">Uploading...</span>
                    ) : (
                      <>
                        <FaCheckCircle /> Submit Agent Profile
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
        )}
      </div>
    </div>
  );
}

export default TravelerToAgent;
